import * as joda from "@js-joda/core";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { Source } from "trader.ts/types/source";
import { scheduleForCleanup } from "trader.ts/ui";
import {
  constantField,
  dateBox,
  Field,
  Form,
  selectBox,
  textBox,
  timeBox,
} from "trader.ts/ui.forms";
import { checkAllCasesHandled, mapPartial } from "trader.ts/utils";
import {
  externalteacherid,
  internalteacherid,
  lessongroupid,
  lessonid,
  teacherid,
} from "../../../sql/classy/types";
import * as style from "../../../styles/style.css.classes";
import { makeArrayField } from "../../client/arrayfield";
import { constantFieldWithRender } from "../../client/constantfieldwithrender";
import { timerangesOverlap } from "../../lib/time";
import { HHmm, renderFirstNameLastName, renderTimeRange } from "../../render";
import * as routes from "../../routes";
import { planning_slot } from "./client";
import { emailValidation } from "../../lib/emailisvalid";
import {
  radioField,
  radioFieldCheckboxRenderInputLike,
} from "../../client/radiofield";

export function renderTeacherAbsenceForm(
  date: joda.LocalDate,
  internalteacherid: internalteacherid,
  sources: { timeS: Source<joda.LocalTime>; durationS: Source<number> },
  slots: planning_slot[],
) {
  const untilS = new Source(
    sources.timeS.get().plusMinutes(sources.durationS.get()),
  );
  scheduleForCleanup(
    untilS.observe((until) => {
      sources.durationS.set(
        joda.Duration.between(sources.timeS.get(), until).toMinutes(),
      );
    }),
  );

  const form = new Form({})
    .addField("date", [], () => constantField(date))
    .addField("internalteacherid", [], () => constantField(internalteacherid))
    .addField("time", [], () =>
      timeBox({
        initialVal: sources.timeS.get(),
        label: "Van",
        previewS: sources.timeS,
      }),
    )
    .addField("until", [], () =>
      timeBox({
        initialVal: untilS.get(),
        previewS: untilS,
        label: "Tot",
      }),
    )
    .addField("duration", ["time", "until"], ({ time, until }) =>
      constantField(joda.Duration.between(time, until).toMinutes()),
    )
    .addField(
      "impacted_lessons",
      ["time", "duration"],
      ({ time, duration }) => {
        const impacted_lessons = mapPartial(slots, (sl) => {
          if (
            sl.contents.tag === "lesson" &&
            sl.contents.lesson.date.isEqual(date) &&
            sl.internalteacherid === internalteacherid &&
            timerangesOverlap(sl, { time, duration })
          ) {
            return sl.contents;
          } else {
            return null;
          }
        }).orderBy((sl) => sl.lesson.time.toSecondOfDay());
        return constantFieldWithRender(
          impacted_lessons,
          impacted_lessons.length === 0 ? (
            <div></div>
          ) : (
            <article>
              <label>{"Geïmpacteerde lessen"}</label>
              <ul>
                {impacted_lessons.map((l) => (
                  <li>
                    {renderTimeRange(
                      l.lesson.time,
                      l.lessongroup.lessonduration,
                    )}
                    : {l.students.map(renderFirstNameLastName).join(", ")}
                  </li>
                ))}
              </ul>
            </article>
          ),
        );
      },
    )
    .addField("action_choice", ["impacted_lessons"], ({ impacted_lessons }) => {
      if (impacted_lessons.length === 0) {
        return constantField("remove_lessons" as const);
      }
      const actions = [
        "internal_replacement" as const,
        "external_replacement" as const,
        "reschedule_lessons" as const,
        "remove_lessons" as const,
      ];
      return selectBox({
        values: actions,
        initial: "internal_replacement",
        show: function showAction(s: (typeof actions)[number]) {
          switch (s) {
            case "internal_replacement":
              return "Interne docent als vervanger aanduiden";
            case "external_replacement":
              return "Externe docent als vervanger aanduiden";
            case "reschedule_lessons":
              return "Inhaallessen plannen";
            case "remove_lessons":
              return "Lessen verwijderen";
            default:
              return checkAllCasesHandled(s);
          }
        },
        label: "Wat wil je doen met deze lessen?",
      });
    })
    .addField(
      "action",
      ["impacted_lessons", "action_choice"],
      async ({
        impacted_lessons,
        action_choice,
      }): Promise<
        Field<
          | {
              tag: "internal_replacement";
              internalteacher: {
                internalteacherid: internalteacherid;
                id: teacherid;
              };
            }
          | {
              tag: "external_replacement";
              externalteacher: {
                choice: "new" | "existing";
                teacher:
                  | {
                      tag: "existing";
                      externalteacherid: externalteacherid;
                      id: teacherid;
                    }
                  | {
                      tag: "new";
                      firstname: string;
                      lastname: string;
                      email: string;
                    };
              };
            }
          | {
              tag: "reschedule_lessons";
              lessons: {
                id: lessonid;
                lessongroupid: lessongroupid;
                date: joda.LocalDate;
                time: joda.LocalTime;
              }[];
            }
          | { tag: "remove_lessons" }
        >
      > => {
        if (impacted_lessons.length === 0) {
          return constantField({ tag: "remove_lessons" as const });
        }
        const firstLessonStart =
          impacted_lessons.first()?.lesson.time || joda.LocalTime.of(0);
        const lastLesson = impacted_lessons.last();
        const lastLessonEnd = lastLesson
          ? lastLesson.lesson.time.plusMinutes(
              lastLesson.lessongroup.lessonduration,
            )
          : joda.LocalTime.of(0);
        const fullLessonsDuration = joda.Duration.between(
          firstLessonStart,
          lastLessonEnd,
        ).toMinutes();

        switch (action_choice) {
          case "internal_replacement": {
            const possibilities = await rpc(
              routes.getPossibleInternalReplacementsSpec,
              undefined,
              {
                internalteacherid,
                date,
                time: firstLessonStart,
                duration: fullLessonsDuration,
                instrumentids: impacted_lessons
                  .map((l) => l.instrument.id)
                  .uniq(),
              },
            ).then((ts) =>
              ts.orderBy(
                [(p) => p.order, renderFirstNameLastName],
                ["asc", "asc"],
              ),
            );
            return new Form({})
              .addField("tag", [], () =>
                constantField("internal_replacement" as const),
              )
              .addField("internalteacher", [], () => {
                return selectBox({
                  values: possibilities,
                  initial: undefined,
                  show: renderFirstNameLastName,
                  label: "Mogelijke docenten",
                  isDisabled: (p) => p.disabled,
                  groups: {
                    groupnames: possibilities
                      .map((s) => s.extraText || "Docent")
                      .uniq(),
                    assignToGroup: (t) => t.extraText || "Docent",
                  },
                });
              })
              .build();
          }
          case "external_replacement": {
            const possibilities = await rpc(
              routes.getPossibleExternalReplacementsSpec,
              undefined,
              {
                date,
                time: firstLessonStart,
                duration: fullLessonsDuration,
              },
            ).then((ts) =>
              ts.orderBy(
                [(p) => p.order, renderFirstNameLastName],
                ["asc", "asc"],
              ),
            );
            return new Form({})
              .addField("tag", [], () =>
                constantField("external_replacement" as const),
              )
              .addField(
                "externalteacher",
                [],
                async (): Promise<
                  Field<{
                    choice: "new" | "existing";
                    teacher:
                      | {
                          tag: "existing";
                          externalteacherid: externalteacherid;
                          id: teacherid;
                        }
                      | {
                          tag: "new";
                          firstname: string;
                          lastname: string;
                          email: string;
                        };
                  }>
                > => {
                  return new Form({})
                    .addField("choice", [], async () => {
                      if (possibilities.length > 0) {
                        return radioField({
                          initial: "existing" as const,
                          values: ["existing" as const, "new" as const],
                          render: radioFieldCheckboxRenderInputLike,
                          label: "Externe docent",
                          containerClass: style.equal_columns_non_responsive,
                          show: (n) =>
                            n === "new"
                              ? "Nieuw"
                              : n === "existing"
                              ? "Bestaande"
                              : checkAllCasesHandled(n),
                        });
                      } else {
                        return constantField("new" as const);
                      }
                    })
                    .addField(
                      "teacher",
                      ["choice"],
                      async ({
                        choice,
                      }): Promise<
                        Field<
                          | {
                              tag: "existing";
                              externalteacherid: externalteacherid;
                              id: teacherid;
                            }
                          | {
                              tag: "new";
                              firstname: string;
                              lastname: string;
                              email: string;
                            }
                        >
                      > => {
                        if (choice === "new") {
                          return new Form({})
                            .addField("tag", [], () =>
                              constantField("new" as const),
                            )
                            .addField("firstname", [], () =>
                              textBox({ label: "Voornaam" }),
                            )
                            .addField("lastname", [], () =>
                              textBox({ label: "Achternaam" }),
                            )
                            .addField("email", [], () =>
                              textBox({
                                label: "Email",
                                validations: [emailValidation],
                              }),
                            )
                            .build();
                        } else {
                          const values = possibilities
                            .map((p) => ({ tag: "existing" as const, ...p }))
                            .orderBy(
                              [(p) => p.order, renderFirstNameLastName],
                              ["asc", "asc"],
                            );
                          return selectBox({
                            values,
                            initial: values[0],
                            show: renderFirstNameLastName,
                            label: "Mogelijke externe docenten",
                            isDisabled: (p) => p.disabled,
                            groups: {
                              groupnames: values
                                .map((s) => s.extraText || "Docent")
                                .uniq(),
                              assignToGroup: (t) =>
                                t.extraText || "Docent",
                            },
                          });
                        }
                      },
                    )
                    .build();
                },
              )
              .build();
          }
          case "reschedule_lessons": {
            return new Form({})
              .addField("tag", [], () =>
                constantField("reschedule_lessons" as const),
              )
              .addField(
                "lessons",
                [],
                async (): Promise<
                  Field<
                    {
                      id: lessonid;
                      lessongroupid: lessongroupid;
                      date: joda.LocalDate;
                      time: joda.LocalTime;
                    }[]
                  >
                > => {
                  const initialDates = await rpc(
                    routes.getCatchupLessonDatesSpec,
                    undefined,
                    {
                      lessons: impacted_lessons.map((l) => ({
                        id: l.lesson.id,
                        date: l.lesson.date,
                        time: l.lessongroup.time,
                        lessonduration: l.lessongroup.lessonduration,
                        lessongroupid: l.lessongroup.id,
                        internalteacherid: internalteacherid,
                      })),
                    },
                  );
                  const impactedWithDates = impacted_lessons.map((l) => ({
                    id: l.lesson.id,
                    lessongroupid: l.lessongroup.id,
                    date: initialDates.find((d) => d.id === l.lesson.id)!
                      .proposedDate,
                    time: l.lessongroup.time,
                  }));
                  return makeArrayField({
                    initialVal: impactedWithDates,
                    makeSubForm: async (t, i) => {
                      return new Form({})
                        .addField("id", [], () => constantField(t.id))
                        .addField("lessongroupid", [], () =>
                          constantField(t.lessongroupid),
                        )
                        .addField("date", [], () =>
                          dateBox({
                            initialVal: t.date,
                            onChange:
                              i === 0
                                ? (d) => {
                                    const inputs =
                                      document.body.querySelectorAll(
                                        'input[type="date"]',
                                      );
                                    inputs.forEach((i) => {
                                      (i as HTMLInputElement).value =
                                        d.toString();
                                      (i as HTMLInputElement).dispatchEvent(
                                        new Event("input"),
                                      );
                                    });
                                  }
                                : undefined,
                          }),
                        )
                        .addField("time", [], () =>
                          timeBox({
                            initialVal: t.time,
                          }),
                        )
                        .build({
                          render: (fields) => {
                            const l = impacted_lessons.find(
                              (l) => l.lesson.id === t.id,
                            )!;
                            return (
                              <tr>
                                <td style="line-height: 1rem; vertical-align: middle;">
                                  <div>{l.lesson.time.format(HHmm)}</div>
                                  <div>
                                    {l.students
                                      .map((s) => s.firstname)
                                      .join(", ")
                                      .slice(0, 10)}
                                  </div>
                                </td>
                                <td>{fields.date()}</td>
                                <td>{fields.time()}</td>
                              </tr>
                            );
                          },
                        });
                    },
                    makeNew: () =>
                      // irrelevant
                      ({
                        id: 0 as lessonid,
                        lessongroupid: 0 as lessongroupid,
                        lessonduration: 0,
                        students: [],
                        date: joda.LocalDate.now(),
                        time: joda.LocalTime.now(),
                      }),
                    render: (fields) => {
                      return (
                        <div>
                          <label>{"Verplaatsingen"}</label>
                          <table className={style.table_has_inputs}>
                            <tr>
                              <th style="padding: 0px; margin: 0px"></th>
                              <th style="padding: 0px; margin: 0px">
                                {"Datum"}
                              </th>
                              <th style="padding: 0px; margin: 0px">
                                {"Tijd"}
                              </th>
                            </tr>
                            {fields.map((field) => field.render())}
                          </table>
                        </div>
                      );
                    },
                  });
                },
              )
              .build();
          }
          case "remove_lessons": {
            return new Form({})
              .addField("tag", [], () =>
                constantField("remove_lessons" as const),
              )
              .build();
          }
          default:
            return checkAllCasesHandled(action_choice);
        }
      },
    );

  return form.build({
    render: (fields) => {
      return (
        <div>
          <div className={style.equal_columns_non_responsive}>
            {fields.time()}
            {fields.until()}
          </div>
          {fields.impacted_lessons()}
          {fields.action_choice()}
          {fields.action()}
        </div>
      );
    },
  });
}
